import { Button, Icon, Tooltip, Typography, withStyles, WithStyles } from '@material-ui/core';
import { IField } from '@pbl/pbl-react-core/lib/models/forms/types';
import { SessionService } from '@pbl/pbl-react-core/lib/services';
import { FieldRenderer } from '@pbl/pbl-react-web-components/lib/package';
import styles from 'assets/jss/modules/login/LoginFormStyle';
import GridContainer from 'components/Grid/GridContainer.jsx';
import GridItem from 'components/Grid/GridItem.jsx';
import {
  loginEmailSchema,
  loginPasswordSchema,
  loginRememberMeSchema,
  loginSchema
} from 'modules/authentication/login/components/validations';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { IRootState } from 'redux/reducers';
import { login } from 'redux/reducers/authentication/actions';
import { AuthService } from 'utils/auth-service';
import AccountReactivateDialog from './AccountReactivateDialog';
import AccountReactivateEmailSentDialog from './AccountReactivateEmailSentDialog';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

interface ILoginFormProps extends StateProps, DispatchProps, RouteComponentProps, WithStyles<typeof styles> {
  successCallback: () => void;
  errorCallback?: () => void;
}

interface ILoginFormState {
  email: string;
  password: string;
  formIsValid: boolean;
  rememberMe: boolean;
  [attr: string]: string | boolean;
  reactivationEmailDialog: boolean;
  buttonLoading: boolean; // Resolves Login issue: DO-28588
}

class LoginForm extends React.Component<ILoginFormProps, ILoginFormState> {
  public state: ILoginFormState = {
    email: '',
    password: '',
    rememberMe: false,
    formIsValid: false,
    reactivationEmailDialog: false,
    buttonLoading: false
  };

  public render() {
    const { email, password, formIsValid, rememberMe } = this.state;
    const { authenticating, classes, show } = this.props;
    const disableLogin = this.state.buttonLoading && authenticating;

    const emailField: IField<string> = {
      type: 'email',
      key: 'email',
      label: 'Email',
      value: email,
      get placeholder(): string {
        return this.label;
      },
      isRequired: true,
      get fieldValue() {
        return this.value;
      },
      validationSchema: loginEmailSchema
    };

    const rememberMeField: IField<boolean> = {
      type: 'boolean',
      key: 'rememberMe',
      label: 'Remember Me',
      value: rememberMe,
      get placeholder(): string {
        return this.label;
      },
      isRequired: true,
      get fieldValue() {
        return this.value;
      },
      validationSchema: loginRememberMeSchema
    };
    const passwordField: IField<string> = {
      type: 'password',
      key: 'password',
      label: 'Password',
      get placeholder(): string {
        return this.label;
      },
      value: password,
      isRequired: true,
      showPassword: true,
      secureTextEntry: true,
      get fieldValue() {
        return this.value;
      },
      validationSchema: loginPasswordSchema
    };

    return (
      <div className={classes.container}>
        <GridContainer justify="center">
          <GridItem sm={10} md={10}>
            <div className={classes.content}>
              <FieldRenderer
                field={emailField}
                onChange={this.onEmailChange}
                inputProps={{
                  autoComplete: `email`,
                  fullWidth: true,
                  onBlur: this.validateForm,
                  variant: 'filled',
                  autoFocus: true
                }}
                InputProps={undefined}
                className={''}
                getGoogleAddressInput={undefined}
                labelProps={undefined}
                onError={undefined}
              />

              <FieldRenderer
                field={passwordField}
                onChange={this.onPasswordChange}
                inputProps={{
                  'aria-live': `assertive`,
                  autoComplete: `password`,
                  fullWidth: true,
                  onBlur: this.validateForm,
                  variant: 'filled',
                  style: { marginTop: 10 },
                  onKeyDown: this.onKeyDown
                }}
                InputProps={undefined}
                className={''}
                getGoogleAddressInput={undefined}
                labelProps={undefined}
                onError={undefined}
              />
              <div className={classes.forgotContainer}>
                <div className={classes.remember}>
                  <FieldRenderer
                    field={rememberMeField}
                    onChange={this.toggleRememberMe}
                    inputProps={{
                      'aria-label': `Remember Me.`,
                      'aria-live': `assertive`,
                      onBlur: this.validateForm,
                      style: { marginTop: 10 }
                    }}
                    InputProps={undefined}
                    className={''}
                    getGoogleAddressInput={undefined}
                    labelProps={undefined}
                    onError={undefined}
                  />
                </div>
                <div className={classes.forgot}>
                  <Tooltip title={'Forgot Password?'} disableFocusListener={true} aria-label={'Forgot Password?'}>
                    <Button variant={'text'} onClick={this.onNavigate.bind(this, '/forgot-password')} aria-label="Forgot Password?">
                      <Typography variant="body2" className={classes.primaryText} style={{ textDecoration: 'none', textTransform: 'none' }}>
                        Forgot Password?
                      </Typography>
                    </Button>
                  </Tooltip>
                </div>
              </div>
              <Button
                size="large"
                variant="contained"
                type="submit" // This is a change I made (might help the password manager)
                fullWidth={true}
                disabled={disableLogin || !formIsValid || email.length === 0 || password.length === 0}
                color="primary"
                onClick={this.onSubmit}
                aria-label="Sign in"
              >
                SIGN In
              </Button>
              <AccountReactivateEmailSentDialog email={email} show={this.state.reactivationEmailDialog} okayCallback={this.okayCallback} />
              <AccountReactivateDialog email={email} show={show} successCalback={this.successCallback.bind(this, email)} />
              <Tooltip title={'Re-send Activation Email?'} disableFocusListener={true} aria-label={'Re-send Activation Email?'}>
                <Button
                  variant={'text'}
                  onClick={this.onNavigate.bind(this, '/activate-email')}
                  aria-label="Re-send Activation Email?"
                  style={{ marginTop: 24 }}
                >
                  <Typography variant="body2" className={classes.primaryText} style={{ textDecoration: 'none', textTransform: 'none' }}>
                    Re-send Activation Email?
                  </Typography>
                </Button>
              </Tooltip>

              <div className={classes.register}>
                <Typography color="textPrimary" variant="body2">
                  Need to create an account?
                </Typography>
                <Tooltip title={'Register Here'} disableFocusListener={true} aria-label={`Register Here`}>
                  <Button onClick={this.onNavigate.bind(this, '/register')} aria-label="Register Here">
                    <Typography variant="body2" className={classes.primaryText}>
                      REGISTER HERE
                    </Typography>
                    <Icon color="primary">how_to_reg</Icon>
                  </Button>
                </Tooltip>
              </div>
            </div>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  private onSubmit = async () => {
    this.setState({ buttonLoading: true });
    try {
      await SessionService.status();
    } catch (e) {
      console.warn(e);
    }
    const { successCallback } = this.props;
    const { email, password, rememberMe } = this.state;

    if (!!email && email.length > 0 && !!password && password.length > 0) {
      await this.props.login(email, password, rememberMe, successCallback);
    }
    this.setState({ buttonLoading: false });
  };

  private onKeyDown = async event => {
    if (event.key === 'Enter') {
      await this.onSubmit();
    }
  };

  private onNavigate = (page: string) => {
    this.props.history.push(page);
  };

  private onEmailChange = (value: any) => {
    const email = document.querySelector<HTMLInputElement>('#email')?.value
      ?? document.querySelector<HTMLInputElement>('input[name="email"]')?.value;

    console.warn(`[onEmailChange] was called: email: ${email}, value: ${value}`);

    this.setState({ email: value });
    this.validateForm();
  };

  private async successCallback(email: string) {
    try {
      await AuthService.unSuspend(email);
      this.setState({ reactivationEmailDialog: true });
    } catch (e) { }
  }

  private okayCallback = () => {
    this.setState({ reactivationEmailDialog: false });
  };
  private onPasswordChange = (value: any) => {
    const password = document.querySelector<HTMLInputElement>('#password')?.value
      ?? document.querySelector<HTMLInputElement>('input[name="password"]')?.value;

    console.warn(`[onPasswordChange] was called: password: ${password}, value: ${value}`);
    this.setState({ password: value }, this.validateForm);
  };
  toggleRememberMe = value => {
    this.setState({ rememberMe: value });
  };

  private validateForm = () => {
    const { email, password, rememberMe } = this.state;
    const formIsValid = loginSchema.isValidSync({ email, password, rememberMe });
    this.setState({ formIsValid });
  };
}

const mapStateToProps = ({ authentication: { authenticating, showSuspendMessage }, accountReactivation: { show } }: IRootState) => ({
  authenticating,
  showSuspendMessage,
  show
});

const mapDispatchToProps = {
  login
};

// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withStyles(styles)(LoginForm)));
